import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { useTemplateColors } from 'PDF/TemplateEngine/TemplateColorsContext';
import { replaceVariables } from 'PDF/TemplateEngine/utils/replaceVariables';

// Footer editable desde el editor low-code. Se monta <View fixed> al pie de
// cada página. Los campos soportan {var} contra `data` y las keys especiales
// {pageNumber} / {totalPages} que @react-pdf/renderer inyecta via render cb.
//
// Config soportado:
//   - leftLine / centerLine / rightLine1 / rightLine2 (string con {var}).
//   - borderColor: hex override del borde superior (fallback colors.border).
const PAGE_NUM_RE = /\{pageNumber\}|\{totalPages\}/;

function interpolateLine(template, data, pageInfo) {
  if (!template) return '';
  // {pageNumber}/{totalPages} se sustituyen ANTES de replaceVariables: esos
  // tokens los inyecta @react-pdf en el render callback, no viven en `data`. Si
  // replaceVariables corre primero los blanquea (decisión 2026-06-20: token sin
  // resolver → ''), y el footer sale "Página  de " en vez de "Página 1 de 3".
  let result = template;
  if (pageInfo) {
    result = result
      .replace(/\{pageNumber\}/g, String(pageInfo.pageNumber))
      .replace(/\{totalPages\}/g, String(pageInfo.totalPages));
  }
  return replaceVariables(result, data || {});
}

function MaybePageText({ template, data, style }) {
  if (!template) return null;
  const usesPageInfo = PAGE_NUM_RE.test(template);
  if (usesPageInfo) {
    return <Text style={style} render={(pageInfo) => interpolateLine(template, data, pageInfo)} />;
  }
  const resolved = replaceVariables(template, data || {});
  return resolved ? <Text style={style}>{resolved}</Text> : null;
}

function FooterSection({ section, data }) {
  const { colors, FONT } = useTemplateColors();
  const cfg = section?.config || {};
  const borderColor = cfg.borderColor || colors.border;

  const styles = useMemo(
    () =>
      StyleSheet.create({
        footer: {
          position: 'absolute',
          bottom: '0.8cm',
          left: '1.5cm',
          right: '1.5cm',
          borderTop: `1px solid ${borderColor}`,
          paddingTop: '0.2cm',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        },
        col: {
          flex: 1,
        },
        center: {
          flex: 1,
          alignItems: 'center',
        },
        rightCol: {
          flex: 1,
          alignItems: 'flex-end',
        },
        text: {
          fontSize: 8,
          fontFamily: FONT.base,
          color: colors.muted,
        },
      }),
    [colors, FONT, borderColor]
  );

  return (
    <View style={styles.footer} fixed>
      <View style={styles.col}>
        <MaybePageText template={cfg.leftLine} data={data} style={styles.text} />
        <MaybePageText template={cfg.leftLine2} data={data} style={styles.text} />
      </View>
      <View style={styles.center}>
        <MaybePageText template={cfg.centerLine} data={data} style={styles.text} />
      </View>
      <View style={styles.rightCol}>
        <MaybePageText template={cfg.rightLine1} data={data} style={styles.text} />
        <MaybePageText template={cfg.rightLine2} data={data} style={styles.text} />
      </View>
    </View>
  );
}

export default FooterSection;
